/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect } from "react";
import { View } from "react-native";
import LottieView from "lottie-react";
import Rodal from "rodal";

import { UpduoButton, Text, styles, ButtonType } from "UpduoLibrary";
import { UpduoSounds } from "config/UpduoSounds";
import QuotaProgressModalBody from "./QuotaProgressModalBody";
import SessionCompleteModalBody from "./SessionCompleteModalBody";
import loadingAnimation from "assets/loading_lottie.json";

export interface SessionProceedModalProps {
  onSessionFinish: () => void;
  completedSessionsCount?: number;
  sessionQuotaCount?: number;
  isVisible: boolean;
  isLoading: boolean;
  skillName: string;
  lastSessionWasCancelled: boolean;
}

enum SessionProceedModalMode {
  SHOW_SESSION_COMPLETE,
  SHOW_PROGRESS,
}

export default function SessionProceedModal({
  onSessionFinish,
  completedSessionsCount,
  sessionQuotaCount,
  isVisible,
  isLoading,
  skillName,
  lastSessionWasCancelled,
}: SessionProceedModalProps) {
  const [mode, setMode] = useState<SessionProceedModalMode>(
    SessionProceedModalMode.SHOW_SESSION_COMPLETE
  );

  const _handleNextScreen = () => {
    setMode(SessionProceedModalMode.SHOW_PROGRESS);
  };

  useEffect(() => {
    // When the modal is shown, start it on SHOW_SESSION_COMPLETE
    setMode(SessionProceedModalMode.SHOW_SESSION_COMPLETE);
    // and play the sound
    if (!isLoading && isVisible) {
      if (lastSessionWasCancelled) {
        UpduoSounds.sessionCancelled.play();
      } else {
        UpduoSounds.sessionComplete.play();
      }
    }
  }, [isLoading, isVisible]);

  const body = (() => {
    if (isLoading) {
      return (
        <>
          <Text isHeading={true} style={styles.modalTitleText}>
            {"Session Processing..."}
          </Text>
          <LottieView
            animationData={loadingAnimation}
            style={{ width: 150 }}
            autoPlay={true}
            loop={true}
          />
        </>
      );
    }

    const showQuotaProgress =
      completedSessionsCount !== null &&
      completedSessionsCount !== undefined &&
      sessionQuotaCount !== null &&
      sessionQuotaCount !== undefined;

    const buttons =
      showQuotaProgress &&
      mode === SessionProceedModalMode.SHOW_SESSION_COMPLETE ? (
        <UpduoButton
          type={ButtonType.SECONDARY}
          onPress={_handleNextScreen}
          text={"Next"}
        />
      ) : (
        <>
          <UpduoButton
            type={ButtonType.SECONDARY}
            onPress={onSessionFinish}
            text={"Finish"}
          />
        </>
      );

    const buttonContainer = (
      <>
        <View style={{ flexGrow: 1, flexBasis: 0 }} />
        <View style={{ height: 96 }}>{buttons}</View>
      </>
    );

    switch (mode) {
      case SessionProceedModalMode.SHOW_SESSION_COMPLETE:
        return (
          <>
            <SessionCompleteModalBody
              skillName={skillName}
              lastSessionWasCancelled={lastSessionWasCancelled}
            />
            {buttonContainer}
          </>
        );
      case SessionProceedModalMode.SHOW_PROGRESS:
        return (
          <>
            <QuotaProgressModalBody
              lastSessionWasCancelled={lastSessionWasCancelled}
              completedSessions={completedSessionsCount || 0}
              sessionQuota={sessionQuotaCount || 0}
            />
            {buttonContainer}
          </>
        );
      default:
        return null;
    }
  })();

  return (
    <Rodal
      visible={isVisible}
      width={330}
      height={365}
      onClose={() => {}}
      customStyles={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        padding: 20,
        borderRadius: 12,
      }}
    >
      {body}
    </Rodal>
  );
}
